import classNames from 'classnames'
import { RefObject, useEffect, useRef, useState } from 'react'

import { EN_US } from '@src/quince-site-common/src/constants/app'
import useIsVisible from '@src/quince-site-common/src/hooks/useIsVisible'
import { useTrueFit } from '@src/quince-site-common/src/hooks/useTrueFit'
import CustomerService from '@src/quince-site-common/src/services/customerService/CustomerService'
import setCookie from '@src/quince-site-common/src/utils/setCookie'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { pick } from '@src/utils/functions/utils'

import useLoginDetails from './hooks/useLoginDetails'
import { EXPERIMENT_KEYS } from './true-fit.helpers'
import styles from './true-fit.module.scss'
import {
  AutoSizeSelectCallbackContext,
  OpenCallbackContext,
  SizeSamplingInfo,
  WidgetResultCallbackContext,
} from './types'

declare global {
  interface Window {
    tfcapi?: (...arg: any) => void
  }
}

interface TrueFitPropTypes {
  productId?: number
  colorId?: string
  useDataRec?: boolean
  useSizeSampling?: boolean
  productSizeSamplingInfo?: SizeSamplingInfo[]
  sizeSamplingClassName?: string
  parentContainerId: string
}

const TrueFitWidget = ({
  productId,
  colorId,
  useDataRec,
  useSizeSampling,
  productSizeSamplingInfo,
  sizeSamplingClassName,
  parentContainerId,
}: TrueFitPropTypes) => {
  const [uuid, setUuid] = useState<string>()
  const { isLoggedIn, customerEmail } = useLoginDetails()
  useTrueFit()

  const attributes =
    isLoggedIn && uuid
      ? {
          'data-userid': uuid,
          'data-styleid': productId?.toString(),
          'data-colorid': colorId,
          'data-locale': EN_US,
        }
      : {
          'data-styleid': productId?.toString(),
          'data-colorid': colorId,
          'data-locale': EN_US,
        }

  const widgetRef: RefObject<HTMLDivElement> = useRef(null)
  const { isVisible } = useIsVisible(widgetRef)

  useEffect(() => {
    if (customerEmail) {
      CustomerService.getUUID(customerEmail)
        .then(uuid => {
          setUuid(uuid)
          setCookie('customer_uuid', uuid)
        })
        .catch((e: unknown) => {
          console.error('Failed while fetching customer uuid', e)
        })
    }
  }, [customerEmail])

  useEffect(() => {
    window.tfcapi?.('calculate')
  }, [])

  useEffect(() => {
    if (useSizeSampling && productSizeSamplingInfo?.length) {
      window.tfcapi?.('sizeSampling', productSizeSamplingInfo)
    }
  }, [productSizeSamplingInfo])

  useEffect(() => {
    if (widgetRef.current && isVisible) {
      trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
        action: 'tf_widget_visible',
        context: {
          productId,
          colorId,
          elementId: parentContainerId,
        },
        value: true,
      })
    }
  }, [widgetRef, isVisible, productId, colorId, parentContainerId])

  useEffect(() => {
    window.tfcapi?.('event', 'tfc-fitrec-product', 'autoSizeSelect', (e: AutoSizeSelectCallbackContext) => {
      const { styleId, size } = e
      trackEvent(TELEMETRY_EVENT_NAMES.TF_SIZE_RECOMMENDATION, {
        styleId,
        size,
        elementId: parentContainerId,
      })
    })

    window.tfcapi?.('event', 'tfc-fitrec-product', 'widgetResult', (e: WidgetResultCallbackContext) => {
      const id = Object.keys(e)[0]
      if (id && e[id]?.isWidgetVisible) {
        trackEvent(TELEMETRY_EVENT_NAMES.TF_WIDGET_LOAD, {
          styleId: id,
          successType: e[id]?.successType,
          elementId: parentContainerId,
        })
      }
    })

    window.tfcapi?.('event', 'tfc-fitrec-register', 'open', (e: OpenCallbackContext) => {
      const { styleId } = e
      trackEvent(TELEMETRY_EVENT_NAMES.TF_WIDGET_OPEN, {
        styleId,
        elementId: parentContainerId,
      })
    })

    window.tfcapi?.('event', 'tfc-fitrec-product', 'experiments', (experiments: any) => {
      trackEvent(TELEMETRY_EVENT_NAMES.TF_EXPERIMENTS, {
        productId,
        colorId,
        elementId: parentContainerId,
        truefitRegistrationTreatment: experiments?.[EXPERIMENT_KEYS.REGISTRATION_TREATMENT],
      })
    })
  }, [])

  return (
    <div ref={widgetRef} className={styles.widget}>
      {useDataRec ? <div className={classNames('tfc-fitrec-product')} data-rec-only="true" {...attributes} /> : null}
      {useSizeSampling ? (
        <div
          className={`${sizeSamplingClassName} tfc-fitrec-size-sampling`}
          {...pick(attributes, ['data-locale', 'data-userid'])}
        />
      ) : null}
      {!useSizeSampling && !useDataRec ? (
        <div className={classNames('tfc-fitrec-product')} {...attributes}></div>
      ) : null}
    </div>
  )
}

export { TrueFitWidget }
